import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface LicenseStatus {
  isLoading: boolean;
  isValid: boolean;
  isExpired: boolean;
  status: string | null;
  expiresAt: string | null;
  daysUntilExpiry: number | null;
  planName: string | null;
}

interface InternalState extends LicenseStatus {
  fetchedForClientId: string | null; // Track which clientId we fetched for
}

/**
 * Hook para verificar o status da licença do cliente logado
 * Valida se a licença está ativa e não expirou
 */
export function useLicenseStatus(clientId: string | null, refreshKey?: number): LicenseStatus & { refetch: () => void } {
  const [internalState, setInternalState] = useState<InternalState>({
    isLoading: true,
    isValid: false,
    isExpired: false,
    status: null,
    expiresAt: null,
    daysUntilExpiry: null,
    planName: null,
    fetchedForClientId: null,
  });

  const mountedRef = useRef(true);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const checkLicenseStatus = async () => {

      // Se não tem clientId, marcar como "sem licença" mas não loading
      if (!clientId) {
        if (mountedRef.current) {
          setInternalState({
            isLoading: false,
            isValid: false,
            isExpired: false,
            status: null,
            expiresAt: null,
            daysUntilExpiry: null,
            planName: null,
            fetchedForClientId: null,
          });
        }
        return;
      }

      // Marcar loading antes do fetch
      if (mountedRef.current) {
        setInternalState(prev => ({
          ...prev,
          isLoading: true,
          fetchedForClientId: null, // Reset to indicate we're fetching
        }));
      }

      try {
        const { data: license, error } = await supabase
          .from('client_licenses')
          .select(`
            id,
            status,
            trial_end_date,
            end_date,
            license_plan:license_plans(name)
          `)
          .eq('client_id', clientId)
          .maybeSingle();

        if (!mountedRef.current) return;

        if (error) {
          console.error('[useLicenseStatus] Erro ao buscar licença:', error);
          setInternalState({
            isLoading: false,
            isValid: false,
            isExpired: false,
            status: null,
            expiresAt: null,
            daysUntilExpiry: null,
            planName: null,
            fetchedForClientId: clientId,
          });
          return;
        }

        if (!license) {
          // Sem licença = inválido
          setInternalState({
            isLoading: false,
            isValid: false,
            isExpired: false,
            status: 'no_license',
            expiresAt: null,
            daysUntilExpiry: null,
            planName: null,
            fetchedForClientId: clientId,
          });
          return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const status = license.status;
        const planName = license.license_plan?.name || null;
        
        // Determinar data de expiração baseado no tipo de licença
        let expirationDate: Date | null = null;
        
        if (status === 'trial' && license.trial_end_date) {
          expirationDate = new Date(license.trial_end_date);
        } else if (status === 'active' && license.end_date) {
          expirationDate = new Date(license.end_date);
        }

        // Calcular dias até expiração
        let daysUntilExpiry: number | null = null;
        let isExpired = false;

        if (expirationDate) {
          expirationDate.setHours(0, 0, 0, 0);
          const diffTime = expirationDate.getTime() - today.getTime();
          daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          isExpired = daysUntilExpiry < 0;
        }

        // Status suspenso, cancelado ou past_due = sempre bloqueado
        // Normalize: aceitar tanto "cancelled" (UK) quanto "canceled" (US)
        const invalidStatuses = ['suspended', 'cancelled', 'canceled', 'past_due'];
        if (invalidStatuses.includes(status)) {
          setInternalState({
            isLoading: false,
            isValid: false,
            isExpired: true,
            status,
            expiresAt: expirationDate?.toISOString() || null,
            daysUntilExpiry,
            planName,
            fetchedForClientId: clientId,
          });
          return;
        }

        // Verificar expiração
        const isValid = !isExpired && (status === 'active' || status === 'trial');

        setInternalState({
          isLoading: false,
          isValid,
          isExpired,
          status,
          expiresAt: expirationDate?.toISOString() || null,
          daysUntilExpiry,
          planName,
          fetchedForClientId: clientId,
        });

      } catch (error) {
        console.error('[useLicenseStatus] Erro:', error);
        if (mountedRef.current) {
          setInternalState({
            isLoading: false,
            isValid: false,
            isExpired: false,
            status: null,
            expiresAt: null,
            daysUntilExpiry: null,
            planName: null,
            fetchedForClientId: clientId,
          });
        }
      }
    };

    checkLicenseStatus();

    return () => {
      mountedRef.current = false;
    };
  }, [clientId, refreshKey, internalRefreshKey]);

  // IMPORTANTE: Derivar isLoading baseado em se o fetch foi feito para o clientId atual
  // Isso evita a race condition onde clientId muda mas o estado ainda é do anterior
  const derivedIsLoading = clientId 
    ? (internalState.fetchedForClientId !== clientId || internalState.isLoading)
    : internalState.isLoading;

  return {
    isLoading: derivedIsLoading,
    isValid: internalState.isValid,
    isExpired: internalState.isExpired,
    status: internalState.status,
    expiresAt: internalState.expiresAt,
    daysUntilExpiry: internalState.daysUntilExpiry,
    planName: internalState.planName,
    refetch,
  };
}
